import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Filter } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DatePicker } from '@/components/ui/date-picker';
import { INVOICE_STATUS_OPTIONS, InvoiceSelect } from './invoice-shared';

export interface InvoiceFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
}

const schema = z
  .object({
    status: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  })
  .refine((v) => !v.startDate || !v.endDate || v.startDate <= v.endDate, {
    message: 'End date must be after the start date',
    path: ['endDate'],
  });
type Values = z.infer<typeof schema>;

const toValues = (f: InvoiceFilters): Values => ({
  status: f.status ?? '',
  startDate: f.startDate ?? '',
  endDate: f.endDate ?? '',
});

interface Props {
  filters: InvoiceFilters;
  onChange: (next: InvoiceFilters) => void;
}

export function InvoiceFilterPopover({ filters, onChange }: Props) {
  const [open, setOpen] = useState(false);
  // Edits stay in the form draft until "Save Filter" validates and applies them
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: toValues(filters),
  });

  const save = handleSubmit((v) => {
    onChange({
      status: v.status || undefined,
      startDate: v.startDate || undefined,
      endDate: v.endDate || undefined,
    });
    setOpen(false);
  });

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) reset(toValues(filters));
      }}
    >
      <DropdownMenuTrigger asChild>
        <button type="button" className="flex h-9 items-center gap-2 rounded-full border border-primary-s500 px-4 text-sm font-medium text-primary-s500 hover:bg-primary-s50">
          <Filter size={15} /> Filter
        </button>
      </DropdownMenuTrigger>
      {/* overflow-visible: the DatePicker calendars render as absolute panels
          inside this menu and would be clipped by the default overflow-hidden */}
      <DropdownMenuContent align="end" className="w-80 overflow-visible p-4">
        <div className="flex flex-col gap-3">
          <label className="text-xs font-medium text-lightGray">Status</label>
          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <InvoiceSelect
                value={field.value ?? ''}
                onChange={field.onChange}
                options={INVOICE_STATUS_OPTIONS}
                emptyLabel="All"
                className="h-9 rounded-lg px-3 py-0 text-sm"
              />
            )}
          />

          <label className="text-xs font-medium text-lightGray">Invoice date</label>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              <Controller
                control={control}
                name="startDate"
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    max={watch('endDate') || undefined}
                    className="h-9 rounded-lg px-3 text-sm"
                  />
                )}
              />
            </div>
            <div className="min-w-0 flex-1">
              <Controller
                control={control}
                name="endDate"
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    min={watch('startDate') || undefined}
                    align="end"
                    className="h-9 rounded-lg px-3 text-sm"
                  />
                )}
              />
            </div>
          </div>
          {errors.endDate && (
            <p className="text-xs text-danger">{errors.endDate.message}</p>
          )}

          <div className="mt-1 flex gap-3">
            <button
              type="button"
              onClick={() => {
                reset(toValues({}));
                onChange({});
                setOpen(false);
              }}
              className="bg-primary-s50 text-primary-s500 flex-1 rounded-full py-2.5 text-sm font-medium transition-colors hover:opacity-80"
            >
              Clear Filter
            </button>
            <button
              type="button"
              onClick={() => void save()}
              className="bg-primary-s500 flex-1 rounded-full py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Save Filter
            </button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
